import React, {
  Fragment,
  MouseEvent,
  ReactElement,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  flexRender,
  Row,
  Cell,
} from '@tanstack/react-table';
import isFunction from 'lodash/isFunction';
import {
  VirtualTableColumnDef as ColumnDef,
  VirtualTableRowProps,
} from 'core/components/VirtualTable/types';
import {
  getTableColumnProps,
  TABLE_ROW_COMMON_CLASSES,
} from 'core/components/VirtualTable/helpers';
import Skeleton from 'core/components/Skeleton';
import noop from 'lodash/noop';
import ToolTip from 'core/components/Tooltip';

type Props<T> = {
  row: Row<T>;
  onClick?: (row: Row<T>) => void;
  rowProps?: VirtualTableRowProps<T>;
  className?: string;
  cellClassName?: ((row: Row<T>, cell: Cell<T, unknown>) => string) | string;
  subRowClassName?: ((row: Row<T>) => string) | string;
  hasSkeleton?: boolean;
};

type CellProps<T> = Cell<T, unknown> & {
  className?: string;
  hasSkeleton?: boolean;
};

function CellRenderer<T>({
  column,
  getContext,
  className,
  hasSkeleton,
}: CellProps<T>) {
  const columnDef = (column.columnDef as ColumnDef<T>);
  const skeleton = columnDef?.skeleton ?? <Skeleton />;
  return (
    <div
      {...getTableColumnProps(column.columnDef, className, hasSkeleton)}
      role="button"
      tabIndex={-1}
      onClick={(e: MouseEvent<HTMLElement>) => {
        if (isFunction(columnDef?.onClick) && !hasSkeleton) {
          columnDef.onClick(e, getContext().row, columnDef);
        }
      }}
      onKeyDown={noop}
    >
      {flexRender(
        hasSkeleton ? skeleton : column.columnDef.cell,
        getContext(),
      ) as ReactElement}
    </div>
  );
}

function useAnimation(
  expandable: boolean,
  isExpanded: boolean,
) {
  const [isTransitioning, setIsTransitioning] = useState(false);

  useLayoutEffect(() => {
    setIsTransitioning(true);
  }, [isExpanded, expandable]);

  return {
    setIsTransitioning,
    isTransitioning,
  };
}

export default function TableRow<T>({
  row,
  className,
  cellClassName,
  subRowClassName,
  onClick,
  rowProps,
  hasSkeleton,
}: Props<T>) {
  const isExpanded = row.getIsExpanded();
  const expandable = row.getCanExpand();
  const subRowCount = row.subRows?.length ?? 0;
  const subRowsRef = useRef<HTMLDivElement>(null);
  const prevExpandedRef = useRef(isExpanded);
  const [expandedHeight, setExpandedHeight] = useState(0);
  const [shouldAnimateHeight, setShouldAnimateHeight] = useState(false);

  const {
    isTransitioning,
    setIsTransitioning,
  } = useAnimation(expandable, isExpanded);

  useLayoutEffect(() => {
    const didToggleExpanded = prevExpandedRef.current !== isExpanded;
    prevExpandedRef.current = isExpanded;

    if (!isExpanded) {
      if (didToggleExpanded) {
        const currentHeight = subRowsRef.current?.scrollHeight ?? 0;
        setShouldAnimateHeight(true);
        setExpandedHeight(currentHeight);
        const frameId = requestAnimationFrame(() => {
          setExpandedHeight(0);
        });
        return () => cancelAnimationFrame(frameId);
      }

      setShouldAnimateHeight(false);
      setExpandedHeight(0);
      return undefined;
    }

    if (didToggleExpanded) {
      setShouldAnimateHeight(true);
      setExpandedHeight(0);
      const frameId = requestAnimationFrame(() => {
        setExpandedHeight(subRowsRef.current?.scrollHeight ?? 0);
      });
      return () => cancelAnimationFrame(frameId);
    }

    setShouldAnimateHeight(false);
    setExpandedHeight(subRowsRef.current?.scrollHeight ?? 0);
    return undefined;
  }, [isExpanded, expandable, subRowCount]);

  const {
    tooltip,
    className: rowPropsClassName,
    onClick: rowPropsOnClick,
    ...restRowProps
  } = rowProps?.(row) ?? {};

  const rowElement = (
    <div
      role="button"
      tabIndex={-1}
      className={clsx(TABLE_ROW_COMMON_CLASSES, className, rowPropsClassName)}
      {...restRowProps}
      onClick={(event: MouseEvent<HTMLDivElement>) => {
        rowPropsOnClick?.(event);
        if (!hasSkeleton) {
          onClick?.(row);
        }
      }}
      onKeyDown={noop}
    >
      {row?.getVisibleCells()?.map((cell) => (
        <CellRenderer
          key={cell.id}
          {...cell}
          hasSkeleton={hasSkeleton}
          className={isFunction(cellClassName) ? cellClassName(row, cell) : cellClassName}
        />
      ))}
    </div>
  );

  return (
    <Fragment key={row.id}>
      {tooltip ? (
        <ToolTip title={tooltip}>
          {rowElement}
        </ToolTip>
      ) : rowElement}
      {expandable && (
        <div
          ref={subRowsRef}
          className={clsx('overflow-hidden', {
            'transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]': shouldAnimateHeight,
          })}
          style={{
            height: isExpanded && !shouldAnimateHeight
              ? 'auto'
              : `${expandedHeight}px`,
          }}
          onTransitionEnd={() => {
            setShouldAnimateHeight(false);
            setTimeout(() => {
              setIsTransitioning(false);
            }, 0);
          }}
        >
          {(isTransitioning || isExpanded) && (row.subRows ?? []).map((item) => (
            <TableRow<T>
              className={clsx(
                className,
                isFunction(subRowClassName) ? subRowClassName(item) : subRowClassName,
              )}
              cellClassName={cellClassName}
              rowProps={rowProps}
              onClick={onClick}
              hasSkeleton={hasSkeleton}
              key={item.id}
              row={item}
            />
          ))}
        </div>
      )}
    </Fragment>
  );
}
